import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Consultant } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../hooks/useToast';

const PlusIcon: React.FC<{className?: string}> = ({className}) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);

const AdminConsultantsPage: React.FC = () => {
    const { data: consultants, setData: setConsultants, isLoading } = useCollection<Consultant>('consultants');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [consultantToDelete, setConsultantToDelete] = useState<Consultant | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { showToast } = useToast();

    const openDeleteModal = (consultant: Consultant) => {
        setConsultantToDelete(consultant);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!consultantToDelete) return;

        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'consultants', consultantToDelete.id));
            setConsultants(prev => prev.filter(m => m.id !== consultantToDelete.id));
            showToast('Consultant removed successfully.', { type: 'success' });
        } catch (error) {
            console.error("Failed to delete consultant:", error);
            showToast("Error: Could not remove the consultant.", { type: 'error' });
        } finally {
            setIsModalOpen(false);
            setConsultantToDelete(null);
            setIsDeleting(false);
        }
    };
    
    return (
        <div>
            <ConfirmModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDelete}
                title="Remove Consultant"
                description={`Are you sure you want to remove ${consultantToDelete?.name} from the database? This action cannot be undone.`}
                isConfirming={isDeleting}
            />

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Manage Consultants</h2>
                <Link to="/admin/consultant/new" className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
                   <PlusIcon className="h-5 w-5 mr-2"/>
                    Add New Consultant
                </Link>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                {isLoading && <p className="p-6 text-center">Loading consultants...</p>}
                {!isLoading && (
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                                    Photo
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Role
                                </th>
                                 <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Company
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                        {(consultants || []).map(consultant => (
                                <tr key={consultant.id}>
                                    <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                                        <img 
                                            src={consultant.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(consultant.name)}&background=e5e7eb&color=4b5563`} 
                                            alt={consultant.name} 
                                            className="w-12 h-12 rounded-full object-cover"
                                        />
                                    </td>
                                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                        <p className="text-gray-900 whitespace-no-wrap font-semibold">
                                            {consultant.name}
                                        </p>
                                    </td>
                                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                        <p className="text-gray-900 whitespace-no-wrap">{consultant.role}</p>
                                    </td>
                                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                        <p className="text-gray-900 whitespace-no-wrap">{consultant.company}</p>
                                    </td>
                                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right whitespace-no-wrap">
                                        <Link to={`/admin/consultant/edit/${consultant.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                            Edit
                                        </Link>
                                        <button onClick={() => openDeleteModal(consultant)} className="text-red-600 hover:text-red-900">
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                 {!isLoading && consultants && consultants.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No consultants found.</p>
                        <p className="mt-2">
                            <Link to="/admin/consultant/new" className="text-indigo-600 hover:underline">
                                Add your first consultant!
                            </Link>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminConsultantsPage;